<template>
  <LayoutMain>
    <template #header>
      <PageHeader :icon="Sparkles" :title="t('spark.title')" />
    </template>

    <!-- Mode Selection Cards with Enhanced Visual States -->
    <div class="mb-6 rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-sm">
      <h2 class="mb-6 text-center font-headline text-headline-md text-primary">
        {{ t('spark.chooseYourSpark') }}
      </h2>

      <!-- Reordered: Explore first (left), Post second (right) -->
      <div class="mx-auto flex max-w-2xl flex-row gap-4">
        <!-- Go Out to Explore Button (LEFT SIDE - PRIMARY) -->
        <button
          type="button"
          class="flex-1 rounded-lg border bg-surface-container-lowest p-4 text-left transition-colors sm:p-6"
          :class="
            selectedMode === 'explore'
              ? 'border-primary bg-primary-fixed'
              : 'border-outline-variant hover:border-primary'
          "
          @click="selectedMode = 'explore'"
        >
          <div class="flex flex-col items-center text-center">
            <div class="mb-2 rounded-lg bg-primary-fixed-dim p-3">
              <Compass class="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 class="font-semibold text-on-surface">
                {{ t('spark.goOutToExplore') }}
              </h3>
              <p class="mt-1 text-xs text-on-surface-variant">
                {{ t('spark.setExploringStatus') }}
              </p>
            </div>
          </div>
        </button>

        <!-- Post Status Button (RIGHT SIDE - SECONDARY) -->
        <button
          type="button"
          class="flex-1 rounded-lg border bg-surface-container-lowest p-4 text-left transition-colors sm:p-6"
          :class="
            selectedMode === 'post'
              ? 'border-primary bg-primary-fixed'
              : 'border-outline-variant hover:border-primary'
          "
          @click="selectedMode = 'post'"
        >
          <div class="flex flex-col items-center text-center">
            <div class="mb-2 rounded-lg bg-primary-fixed-dim p-3">
              <MessageSquare class="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 class="font-semibold text-on-surface">
                {{ t('spark.postStatus') }}
              </h3>
              <p class="mt-1 text-xs text-on-surface-variant">
                {{ t('spark.shareYourStatus') }}
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Content Forms (existing logic remains the same) -->
    <div
      v-if="selectedMode"
      class="space-y-4 rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-sm"
    >
      <!-- Explore Form -->
      <div v-if="selectedMode === 'explore'" class="space-y-6">
        <div class="space-y-4">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label html-for="explorationDuration" class="text-on-surface">{{
                t('spark.explorationDuration')
              }}</Label>
              <span id="durationValue" class="text-sm font-semibold text-primary">{{ exploreDuration }} {{ t('spark.minutes') }}</span>
            </div>
            <Range
              id="explorationDuration"
              v-model="exploreDuration"
              name="explorationDuration"
              :min="30"
              :max="120"
              :step="15"
              color="accent"
              class="w-full"
              @input="updateSliderProgress"
            />
            <p class="text-xs text-on-surface-variant">
              {{ t('spark.profileVisibleFor') }} {{ exploreDuration }} {{ t('spark.minutes') }}.
              {{ t('spark.maxMinutes') }}
            </p>
          </div>

          <div>
            <label for="exploreMessage" class="mb-2 block text-sm font-medium text-on-surface">{{
              t('spark.optionalMessage')
            }}</label>
            <Textarea
              id="exploreMessage"
              v-model="exploreMessage"
              name="exploreMessage"
              :placeholder="t('spark.exploreMessagePlaceholder')"
              rows="4"
            />
          </div>

          <Button
            :disabled="isStartingExploration"
            variant="primary"
            class="w-full py-4 text-lg font-semibold"
            @click="handleStartExploring"
          >
            {{
              isStartingExploration
                ? t('spark.starting')
                : `🚀 ${t('spark.startExploring')} (${exploreDuration} ${t('spark.minutes')})`
            }}
          </Button>
        </div>
      </div>

      <!-- Post Form -->
      <div v-if="selectedMode === 'post'" class="space-y-4">
        <Textarea
          ref="textareaRef"
          v-model="postContent"
          :placeholder="t('spark.postPlaceholder')"
          rows="6"
          @input="adjustTextareaHeight"
        />

        <div class="flex items-center justify-between text-sm text-on-surface-variant">
          <span>{{ postContent.length }}/500</span>
          <span v-if="postContent.length > 450" class="text-tertiary">
            {{ 500 - postContent.length }} {{ t('spark.charactersRemaining') }}
          </span>
        </div>

        <Button
          :disabled="!postContent.trim() || isPosting"
          variant="primary"
          class="w-full py-4 text-lg font-semibold"
          @click="handlePost"
        >
          {{ isPosting ? t('spark.posting') : `📤 ${t('spark.shareStatusButton')}` }}
        </Button>
      </div>
    </div>

    <div v-if="error" class="rounded-lg border border-outline-variant bg-surface-container-lowest p-4 shadow-sm">
      <ErrorDisplay :message="error" />
    </div>
  </LayoutMain>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import { supabase } from '../lib/supabase'
import { Sparkles, MessageSquare, Compass } from 'lucide-vue-next'
import Button from '../components/ui/Button.vue'
import Textarea from '../components/ui/Textarea.vue'
import ErrorDisplay from '../components/ui/ErrorDisplay.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import Label from '../components/ui/Label.vue'
import Range from '../components/ui/Range.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const userStore = useUserStore()
const { t } = useI18n()

// DEFAULT TO EXPLORE MODE - KEY CHANGE
const selectedMode = ref('explore') // Changed from '' to 'explore'

// Post status variables
const postContent = ref('')
const isPosting = ref(false)
const textareaRef = ref(null)

// Explore mode variables
const exploreDuration = ref(90) // default to 90 minutes
const exploreMessage = ref('')
const isStartingExploration = ref(false)

// Common variables
const error = ref('')

// Functions remain the same...

// Slider style (unchanged)
const sliderStyle = computed(() => {
  const min = 30
  const max = 120
  const value = exploreDuration.value
  const progress = ((value - min) / (max - min)) * 100

  return {
    background: `linear-gradient(to right, 
      var(--color-accent) 0%, 
      var(--color-accent) ${progress}%, 
      var(--color-neutral-base) ${progress}%, 
      var(--color-neutral-base) 100%)`
  }
})

// Auto-focus enhancement
onMounted(() => {
  // Small delay to ensure smooth page load
  setTimeout(() => {
    // Optional: Add a subtle entrance animation or auto-focus logic here
  }, 100)
})

function adjustTextareaHeight() {
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
    textareaRef.value.style.height = textareaRef.value.scrollHeight + 'px'
  }
}

function updateSliderProgress() {
  // The computed property will automatically update the background
  // This function is called on input to ensure reactivity
}

async function handlePost() {
  if (!postContent.value.trim()) return

  if (postContent.value.length > 500) {
    error.value = t('spark.postTooLong')
    return
  }

  isPosting.value = true
  error.value = ''

  try {
    // Create the regular post
    const { error } = await supabase.from('feed_posts').insert({
      user_id: userStore.profile.id,
      content: postContent.value.trim(),
      post_type: 'regular'
    })

    if (error) {
      throw error
    }

    // Navigate back to feed
    router.push('/')
  } catch (err) {
    error.value = t('spark.postCreationFailed')
    console.error('Post creation error:', err)
  } finally {
    isPosting.value = false
  }
}

async function handleStartExploring() {
  isStartingExploration.value = true
  error.value = ''

  try {
    // Call the user store function to start exploring with optional message
    await userStore.toggleZinInSpel(exploreMessage.value, exploreDuration.value)

    // If there's an exploration message, create a feed post
    if (exploreMessage.value && exploreMessage.value.trim()) {
      await createExplorationFeedPost(exploreMessage.value.trim())
    }

    // Navigate to the exploring view to show the user is now exploring
    router.push('/exploring')
  } catch (err) {
    error.value = t('spark.explorationFailed')
    console.error('Start exploring error:', err)
  } finally {
    isStartingExploration.value = false
  }
}

async function createExplorationFeedPost(message) {
  try {
    const { error } = await supabase.from('feed_posts').insert({
      user_id: userStore.profile.id,
      content: `${t('spark.outExploring')} ${message}`,
      post_type: 'exploration'
    })

    if (error) {
      console.error('Failed to create exploration feed post:', error)
      // Don't throw error here as the main exploration functionality should still work
    }
  } catch (err) {
    console.error('Error creating exploration feed post:', err)
  }
}
</script>

<style scoped>
textarea {
  font-family: inherit;
  line-height: 1.5;
}

/* Custom slider styling */
.exploration-slider {
  height: 8px;
  appearance: none;
  border-radius: 8px;
  outline: none;
  border: 2px solid var(--color-gray-200);
  transition: all 0.2s ease;
}

.exploration-slider:hover {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-light);
}

.exploration-slider:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-medium);
}

/* Webkit browsers (Chrome, Safari, Edge) */
.exploration-slider::-webkit-slider-track {
  height: 8px;
  border-radius: 8px;
  background: var(--color-neutral-base);
  border: 2px solid var(--color-gray-200);
}

.exploration-slider::-webkit-slider-thumb {
  appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--color-accent);
  border: 3px solid var(--color-white);
  box-shadow: 0 2px 4px var(--shadow-light);
  cursor: pointer;
  transition: all 0.2s ease;
}

.exploration-slider::-webkit-slider-thumb:hover {
  background: var(--color-accent-hover);
  transform: scale(1.1);
  box-shadow: 0 3px 6px var(--shadow-medium);
}

/* Firefox */
.exploration-slider::-moz-range-track {
  height: 8px;
  border-radius: 8px;
  background: var(--color-neutral-base);
  border: 2px solid var(--color-gray-200);
  border: none;
}

.exploration-slider::-moz-range-thumb {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--color-accent);
  border: 3px solid var(--color-white);
  box-shadow: 0 2px 4px var(--shadow-light);
  cursor: pointer;
  border: none;
}

.exploration-slider::-moz-range-thumb:hover {
  background: var(--color-accent-hover);
  transform: scale(1.1);
}
</style>
